import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';

import { environment } from '@environments/environment';
import { Provider } from '@models/provider.model';
import { SCHEDULE_STATUS, ScheduleReportModalData, ScheduleResponse } from '@models/report-template.model';
import { ReportingActions } from '@store/actions/reporting/reporting.actions';
import { selectProviders as selectClinicianProviders } from '@store/selectors/clinician-details/clinician-details.selectors';
import { selectIsSavingSchedule, selectScheduleDuplicateError } from '@store/selectors/reporting/reporting.selectors';
import { selectActiveUser } from '@store/selectors/user/user.selectors';

interface ProviderOption {
  id: string;
  label: string;
  email: string | undefined;
}

interface EmailOption {
  value: string;
  label: string;
}

const PROVIDER_ORG_VALUE = 'Organization';
const PROVIDER_SELECT_ALL = 'SELECT_ALL';

const CADENCE_OPTIONS = [
  { value: 'Monthly(Start)', label: 'Monthly(Start)' },
  { value: 'Monthly(End)', label: 'Monthly(End)' },
  { value: 'Quarterly', label: 'Quarterly' },
];

function endAfterStartValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const group = control as FormGroup;
    const startDate = group.get('startDate')?.value;
    const endDate = group.get('endDate')?.value;
    if (startDate && endDate && new Date(endDate) <= new Date(startDate)) {
      return { endBeforeStart: true };
    }
    return null;
  };
}

@Component({
  selector: 'app-schedule-report-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatCheckboxModule,
    MatChipsModule,
    MatIconModule,
    NgxMatSelectSearchModule,
  ],
  templateUrl: './schedule-report-modal.component.html',
  styleUrl: './schedule-report-modal.component.scss',
})
export class ScheduleReportModalComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly fb = inject(FormBuilder);
  private readonly dialogRef = inject(MatDialogRef<ScheduleReportModalComponent>);
  private readonly actions$ = inject(Actions);
  readonly data: ScheduleReportModalData = inject(MAT_DIALOG_DATA);

  private readonly destroy$ = new Subject<void>();

  readonly cadenceOptions = CADENCE_OPTIONS;

  isScheduleActive = !this.data.schedule || this.data.schedule.statusId === SCHEDULE_STATUS.ACTIVE;

  get isStatusChanged(): boolean {
    if (!this.isEditMode || !this.data.schedule) return false;
    const wasActive = this.data.schedule.statusId === SCHEDULE_STATUS.ACTIVE;
    return this.isScheduleActive !== wasActive;
  }
  readonly minStartDate: Date = (() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow;
  })();
  readonly isEditMode = !!this.data.schedule;
  readonly allowProviderEmailRecipients = environment.allowProviderEmailRecipients;

  // Provider dropdown (shared options list; single-select in edit, multi-select in create)
  providerOptions: ProviderOption[] = [{ id: PROVIDER_ORG_VALUE, label: 'Organization', email: undefined }];
  filteredProviderOptions: ProviderOption[] = [{ id: PROVIDER_ORG_VALUE, label: 'Organization', email: undefined }];
  providerSearchControl = new FormControl<string>('');
  protected allProviders: ProviderOption[] = [];
  private lastProviderSelection: string[] = [PROVIDER_ORG_VALUE];

  // Email dropdown
  allEmailOptions: EmailOption[] = [];
  filteredEmailOptions: EmailOption[] = [];
  emailSearchControl = new FormControl<string>('');
  private lastEmailSelection: string[] = [];

  // Custom email entry
  customEmailControl = new FormControl<string>('', [Validators.email]);
  customEmails: string[] = [];

  userEmail = '';

  notifyEachProvider = false;
  private isStatusSaveInProgress = false;

  get isOrganizationSelected(): boolean {
    if (this.isEditMode) {
      return (this.form.get('providerId')?.value ?? '') === PROVIDER_ORG_VALUE;
    }
    const providerIds: string[] = this.form.get('providerIds')?.value ?? [];
    return providerIds.includes(PROVIDER_ORG_VALUE);
  }


  get showNotifyEachProvider(): boolean {
    const providerIds: string[] = (this.form.get('providerIds')?.value ?? []).filter((v: string) => v !== PROVIDER_SELECT_ALL);
    return !providerIds.includes(PROVIDER_ORG_VALUE) && providerIds.length > 1;
  }

  get triggerLabel(): string {
    const values: string[] = (this.form.get('providerIds')?.value ?? []).filter((v: string) => v !== PROVIDER_SELECT_ALL);
    if (values.includes(PROVIDER_ORG_VALUE)) return 'Organization';
    if (values.length === 0) return '';
    if (values.length === 1) return this.allProviders.find((p) => p.id === values[0])?.label ?? values[0];
    return `${values.length} providers`;
  }

  isSaving$ = this.store.select(selectIsSavingSchedule);
  isDuplicate$ = this.store.select(selectScheduleDuplicateError);

  form: FormGroup = this.fb.group(
    {
      scheduleName: ['', Validators.required],
      cadence: ['', Validators.required],
      // Edit mode: single-select string. Create mode: disabled/unused.
      providerId: [PROVIDER_ORG_VALUE, this.isEditMode ? Validators.required : null],
      // Create mode: multi-select string[]. Edit mode: disabled/unused.
      providerIds: [[PROVIDER_ORG_VALUE], !this.isEditMode ? Validators.required : null],
      startDate: [null, [Validators.required, this.startDateAfterTodayValidator()]],
      endDate: [null, Validators.required],
      emailRecipients: [[]],
    },
    { validators: endAfterStartValidator() }
  );

  get isAllSelected(): boolean {
    return (this.form.get('emailRecipients')?.value ?? []).includes('ALL');
  }

  get dropdownSelectedEmails(): string[] {
    const selected: string[] = this.form.get('emailRecipients')?.value ?? [];
    return selected.filter((v: string) => v !== 'ALL');
  }

  get hasAnyEmails(): boolean {
    return this.isAllSelected || this.dropdownSelectedEmails.length > 0 || this.customEmails.length > 0;
  }

  get resolvedEmails(): string[] {
    const selected: string[] = this.form.get('emailRecipients')?.value ?? [];
    const fromDropdown = selected.includes('ALL')
      ? this.allEmailOptions.map((o) => o.value)
      : selected.filter(Boolean);
    return [...new Set([...fromDropdown, ...this.customEmails])];
  }

  ngOnInit(): void {
    this.actions$.pipe(
      ofType(
        ReportingActions.createScheduleSuccess,
        ReportingActions.updateScheduleSuccess,
      ),
      take(1),
      takeUntil(this.destroy$)
    ).subscribe(() => this.dialogRef.close(true));

    this.store.select(selectActiveUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.userEmail = user.email ?? '';
      });

    this.store.select(selectClinicianProviders)
      .pipe(takeUntil(this.destroy$))
      .subscribe((providers) => {
        const activeProviders = (providers ?? []).filter((p: Provider) => p.active === 1);
        this.allProviders = [
          { id: PROVIDER_ORG_VALUE, label: 'Organization', email: undefined },
          ...activeProviders.map((p: Provider) => ({
            id: String(p.id),
            label: `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim(),
            email: p.providerUserEmailAddress,
          })),
        ];
        this.providerOptions = this.allProviders;
        this.allEmailOptions = this.allowProviderEmailRecipients
          ? activeProviders
              .filter((p: Provider) => p.providerUserEmailAddress)
              .map((p: Provider) => ({
                value: p.providerUserEmailAddress!,
                label: `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim(),
              }))
          : [];
        this.applyProviderSearch(this.providerSearchControl.value ?? '');
        this.applyEmailSearch(this.emailSearchControl.value ?? '');

        if (this.isEditMode) {
          this.prefillForm(this.data.schedule!);
        } else {
          this.setDefaultEmailsForMultiple();
        }
      });

    this.providerSearchControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((text) => this.applyProviderSearch(text ?? ''));

    this.emailSearchControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((text) => this.applyEmailSearch(text ?? ''));

    if (this.isEditMode) {
      this.form.get('providerId')?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((providerId) => {
          this.setDefaultEmails(providerId);
        });
    }

    this.isDuplicate$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isDuplicate) => {
        if (isDuplicate) {
          const providerControl = this.isEditMode ? 'providerId' : 'providerIds';
          this.form.get(providerControl)?.setErrors({ duplicate: true });
          this.form.get('cadence')?.setErrors({ duplicate: true });
        }
      });

  }

  private parseIsoDate(dateStr: string): Date {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private localDateToIso(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private prefillForm(schedule: ScheduleResponse): void {
    this.form.patchValue({
      scheduleName: schedule.scheduleName,
      cadence: schedule.cadence ?? '',
      providerId: schedule.providerId ?? PROVIDER_ORG_VALUE,
      startDate: schedule.startDate ? this.parseIsoDate(schedule.startDate) : null,
      endDate: schedule.endDate ? this.parseIsoDate(schedule.endDate) : null,
    });

    if (schedule.emailRecipients) {
      const savedEmails = schedule.emailRecipients.split(',').map((e) => e.trim()).filter(Boolean);
      if (this.allowProviderEmailRecipients) {
        const knownEmails = new Set(this.allEmailOptions.map((o) => o.value));
        const fromDropdown = savedEmails.filter((e) => knownEmails.has(e) || e === this.userEmail);
        this.customEmails = savedEmails.filter((e) => !fromDropdown.includes(e));
        this.form.get('emailRecipients')?.setValue(fromDropdown);
        this.lastEmailSelection = fromDropdown;
      } else {
        const allowedEmails = savedEmails.filter((e) => e === this.userEmail);
        this.form.get('emailRecipients')?.setValue(allowedEmails);
        this.lastEmailSelection = allowedEmails;
      }
    }
  }

  private setDefaultEmails(providerId: string): void {
    const provider = this.allProviders.find((o) => o.id === providerId);
    const defaultEmail = this.allowProviderEmailRecipients
      ? (provider?.email ?? this.userEmail)
      : this.userEmail;
    const newValue = defaultEmail ? [defaultEmail] : [];
    this.form.get('emailRecipients')?.setValue(newValue);
    this.customEmails = [];
    this.lastEmailSelection = newValue;
  }

  private setDefaultEmailsForMultiple(): void {
    const newValue = this.userEmail ? [this.userEmail] : [];
    this.form.get('emailRecipients')?.setValue(newValue);
    this.customEmails = [];
    this.lastEmailSelection = newValue;
  }

  private applyProviderSearch(text: string): void {
    const q = text.toLowerCase();
    this.filteredProviderOptions = this.providerOptions.filter(
      (o) => o.id === PROVIDER_ORG_VALUE || o.label.toLowerCase().includes(q)
    );
  }

  private applyEmailSearch(text: string): void {
    const q = text.toLowerCase();
    this.filteredEmailOptions = this.allEmailOptions.filter(
      (o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q)
    );
  }

  onProviderSelectionChange(values: string[]): void {
    const individualIds = this.allProviders.filter((o) => o.id !== PROVIDER_ORG_VALUE).map((o) => o.id);
    const hadSelectAll = this.lastProviderSelection.includes(PROVIDER_SELECT_ALL);
    const hasSelectAll = values.includes(PROVIDER_SELECT_ALL);

    let newValue: string[];

    if (hasSelectAll && !hadSelectAll) {
      newValue = [PROVIDER_SELECT_ALL, ...individualIds];
    } else if (!hasSelectAll && hadSelectAll) {
      newValue = [];
    } else {
      const withoutSentinel = values.filter((v) => v !== PROVIDER_SELECT_ALL);
      const hadAll = this.lastProviderSelection.includes(PROVIDER_ORG_VALUE);
      const hasAll = withoutSentinel.includes(PROVIDER_ORG_VALUE);

      if (hasAll && !hadAll) {
        newValue = [PROVIDER_ORG_VALUE];
      } else if (hasAll && withoutSentinel.length > 1) {
        const individuals = withoutSentinel.filter((v) => v !== PROVIDER_ORG_VALUE);
        const allAreSelected = individualIds.length > 0 && individualIds.every((id) => individuals.includes(id));
        newValue = allAreSelected ? [PROVIDER_SELECT_ALL, ...individuals] : individuals;
      } else {
        const allAreSelected = individualIds.length > 0 && individualIds.every((id) => withoutSentinel.includes(id));
        newValue = allAreSelected ? [PROVIDER_SELECT_ALL, ...withoutSentinel] : withoutSentinel;
      }
    }

    this.form.get('providerIds')?.setValue(newValue, { emitEvent: false });
    this.lastProviderSelection = newValue;
    if (!this.showNotifyEachProvider) {
      this.notifyEachProvider = false;
    }
  }

  onEmailSelectionChange(values: string[]): void {
    const hadAll = this.lastEmailSelection.includes('ALL');
    const hasAll = values.includes('ALL');
    if (hasAll && !hadAll) {
      this.form.get('emailRecipients')?.setValue(['ALL'], { emitEvent: false });
    } else if (hasAll && values.length > 1) {
      this.form.get('emailRecipients')?.setValue(values.filter((v) => v !== 'ALL'), { emitEvent: false });
    }
    this.lastEmailSelection = this.form.get('emailRecipients')?.value ?? [];
  }

  addCustomEmail(): void {
    const email = (this.customEmailControl.value ?? '').trim();
    if (!email || this.customEmailControl.invalid) return;
    if (!this.customEmails.includes(email) && !this.resolvedEmails.includes(email)) {
      this.customEmails = [...this.customEmails, email];
    }
    this.customEmailControl.setValue('');
  }

  removeEmail(email: string): void {
    if (this.customEmails.includes(email)) {
      this.customEmails = this.customEmails.filter((e) => e !== email);
      return;
    }
    const current: string[] = this.form.get('emailRecipients')?.value ?? [];
    if (current.includes('ALL')) {
      const expanded = this.allEmailOptions.map((o) => o.value).filter((e) => e !== email);
      this.form.get('emailRecipients')?.setValue(expanded);
    } else {
      this.form.get('emailRecipients')?.setValue(current.filter((e) => e !== email));
    }
    this.lastEmailSelection = this.form.get('emailRecipients')?.value ?? [];
  }

  clearAllEmails(): void {
    this.form.get('emailRecipients')?.setValue([]);
    this.customEmails = [];
    this.lastEmailSelection = [];
  }

  onSave(): void {
    if (this.isEditMode) {
      const wasActive = this.data.schedule!.statusId === SCHEDULE_STATUS.ACTIVE;
      const statusChanged = this.isScheduleActive !== wasActive;

      if (statusChanged) {
        if (this.isStatusSaveInProgress) return;
        this.isStatusSaveInProgress = true;

        if (!this.isScheduleActive) {
          this.store.dispatch(ReportingActions.deleteSchedule({ id: this.data.schedule!.id }));
        } else {
          this.store.dispatch(ReportingActions.reactivateSchedule({ id: this.data.schedule!.id }));
        }

        if (this.form.valid && this.form.dirty) {
          this.actions$.pipe(
            ofType(ReportingActions.deleteScheduleSuccess, ReportingActions.reactivateScheduleSuccess),
            take(1),
            takeUntil(this.destroy$)
          ).subscribe(() => this.dispatchFormAction());
        } else {
          this.actions$.pipe(
            ofType(ReportingActions.deleteScheduleSuccess, ReportingActions.reactivateScheduleSuccess),
            take(1),
            takeUntil(this.destroy$)
          ).subscribe(() => this.dialogRef.close(true));
        }
        return;
      }
    }

    if (this.form.invalid) return;
    this.dispatchFormAction();
  }

  private dispatchFormAction(): void {
    ['cadence', 'providerId', 'providerIds'].forEach((name) => {
      const ctrl = this.form.get(name);
      if (ctrl?.hasError('duplicate')) ctrl.setErrors(null);
    });

    const { cadence, providerId, providerIds, startDate, endDate } = this.form.value;
    const scheduleName = (this.form.value.scheduleName as string)?.trim() ?? '';
    const emailRecipientsStr = this.resolvedEmails.length > 0 ? this.resolvedEmails.join(',') : null;
    const startIso = this.localDateToIso(startDate as Date);
    const endIso = this.localDateToIso(endDate as Date);
    const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (this.isEditMode) {
      this.store.dispatch(
        ReportingActions.updateSchedule({
          id: this.data.schedule!.id,
          request: { cadence, providerId, startDate: startIso, endDate: endIso, timeZoneId, scheduleName, emailRecipients: emailRecipientsStr },
        })
      );
    } else {
      const providers = (providerIds as string[]).filter((id) => id !== PROVIDER_SELECT_ALL).map((id) => ({
        id,
        email: this.notifyEachProvider
          ? (this.allProviders.find((p) => p.id === id)?.email ?? null)
          : null,
      }));
      this.store.dispatch(
        ReportingActions.createSchedule({
          request: {
            reportName: this.data.template.title,
            cadence,
            providers,
            startDate: startIso,
            endDate: endIso,
            timeZoneId,
            scheduleName,
            emailRecipients: emailRecipientsStr,
          },
        })
      );
    }
  }

  private startDateAfterTodayValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const selected = new Date(control.value);
      selected.setHours(0, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return selected <= today ? { startInPast: true } : null;
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
